import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

/** BE `SelectionRequest.reason` 한도(1..240, 문자 수). */
export const ROOM_SELECTION_REASON_MAX = 240;

export type RoomSelectionStatus = 'SELECTED' | 'NOT_SELECTED' | 'UNKNOWN';

/** GET admin/rooms/{id}/ownerships 중 선정 판단에 필요한 사실만 읽은 결과. */
export interface RoomSelectionState {
  roomId: string;
  status: RoomSelectionStatus;
  /** null은 선정 기록이 아직 없음. 이 경우 최초 선정의 expected_version은 0이다. */
  version: number | null;
  /** 단위별 관리권. null은 아직 활성 전이다. */
  unitOwnerships: { unitId: string; ownershipStatus: string | null }[];
}

export interface RoomSelectionCommand {
  reason: string;
  expected_version: number;
}

export interface RoomSelectionReceipt {
  room_id: string;
  selected: true;
  version: number;
}

const invalid = (message: string) =>
  new ApiError({ kind: 'invalid-response', service: 'core', message });
const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const version = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;

/** 최초 선정은 0, 그 외에는 관측한 현재 버전이 expected_version이다. */
export const expectedSelectionVersion = (state: Pick<RoomSelectionState, 'version'>) =>
  state.version ?? 0;

export const roomSelectionReasonLength = (reason: string) => Array.from(reason.trim()).length;

export async function readRoomSelectionState(
  client: ServiceClient,
  roomId: string,
  signal?: AbortSignal,
): Promise<RoomSelectionState> {
  const failure = () => invalid('객실 관리 대상 선정 상태를 확인하지 못했습니다.');
  if (!isUuid(roomId)) throw failure();
  const data = await client.get<unknown>(`admin/rooms/${roomId}/ownerships`, { signal });
  if (
    !record(data) ||
    data.room_id !== roomId ||
    !isOffsetDateTime(data.observed_at) ||
    !Array.isArray(data.units) ||
    (data.selection_status !== 'SELECTED' &&
      data.selection_status !== 'NOT_SELECTED' &&
      data.selection_status !== 'UNKNOWN')
  )
    throw failure();
  const status = data.selection_status;
  // UNKNOWN과 version null은 같이 움직인다. 어긋나면 상태를 추측하지 않는다.
  if ((status === 'UNKNOWN') !== (data.selection_version === null)) throw failure();
  if (data.selection_version !== null && !version(data.selection_version)) throw failure();
  const unitOwnerships = data.units.map((unit) => {
    if (!record(unit) || !isUuid(unit.unit_id)) throw failure();
    if (unit.ownership === null) return { unitId: unit.unit_id, ownershipStatus: null };
    if (!record(unit.ownership) || typeof unit.ownership.status !== 'string') throw failure();
    return { unitId: unit.unit_id, ownershipStatus: unit.ownership.status };
  });
  return {
    roomId,
    status,
    version: data.selection_version,
    unitOwnerships,
  };
}

/**
 * POST admin/rooms/{id}/selection. 본문은 reason·expected_version 두 키뿐이며 command_id는 없다.
 * 같은 행위자·같은 사유의 재요청은 BE가 현재 행을 그대로 돌려주므로(멱등) 응답 version은
 * expected_version+1이 아닐 수 있다.
 */
export async function selectRoom(
  client: ServiceClient,
  roomId: string,
  command: RoomSelectionCommand,
): Promise<RoomSelectionReceipt> {
  const reason = command.reason.trim();
  const length = roomSelectionReasonLength(reason);
  if (
    !isUuid(roomId) ||
    length < 1 ||
    length > ROOM_SELECTION_REASON_MAX ||
    !version(command.expected_version)
  )
    throw invalid('객실 관리 대상 선정 요청이 올바르지 않습니다.');
  const result = await client.post<unknown>(`admin/rooms/${roomId}/selection`, {
    json: { reason, expected_version: command.expected_version },
    expectedStatus: 200,
  });
  if (
    !record(result) ||
    Object.keys(result).length !== 3 ||
    result.room_id !== roomId ||
    result.selected !== true ||
    !version(result.version) ||
    result.version < Math.max(1, command.expected_version)
  )
    throw invalid('객실 관리 대상 선정 결과를 확인하지 못했습니다.');
  return result as unknown as RoomSelectionReceipt;
}
